import React from "react";
import {
  Box,
  Text,
  Flex,
  Avatar,
  useColorModeValue,
  HStack,
  Badge,
  VStack,
} from "@chakra-ui/react";
import { ChatMessage as ChatMessageType } from "../chatbot";
import moment from "moment";
import ReactMarkdown from "react-markdown";
import { MdAttachFile } from "react-icons/md";

interface ChatMessageProps {
  message: ChatMessageType;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.by === "user";

  const bgColor = useColorModeValue(
    isUser ? "blue.50" : "gray.50",
    isUser ? "blue.900" : "gray.700"
  );
  const textColor = useColorModeValue("gray.800", "white");
  const avatarBg = useColorModeValue(
    isUser ? "blue.500" : "gray.500",
    isUser ? "blue.300" : "gray.300"
  );
  const timestampColor = useColorModeValue("gray.500", "gray.400");

  const codeBg = useColorModeValue("gray.100", "gray.600");
  const blockquoteBorderColor = useColorModeValue("gray.300", "gray.500");

  return (
    <Flex w="100%" justify={isUser ? "flex-end" : "flex-start"} mb={4} px={2}>
      <Flex
        maxW="70%"
        flexDirection={isUser ? "row-reverse" : "row"}
        align="flex-start"
        gap={3}
      >
        <Avatar
          size="sm"
          bg={avatarBg}
          name={isUser ? "User" : "AI"}
          color="white"
        />

        <Box
          bg={bgColor}
          p={3}
          borderRadius="lg"
          borderTopLeftRadius={isUser ? "lg" : "sm"}
          borderTopRightRadius={isUser ? "sm" : "lg"}
          minW="120px"
        >
          <VStack spacing={2} align="stretch">
            <Box>
              {isUser ? (
                <Text color={textColor} fontSize="sm">
                  {message.message}
                </Text>
              ) : (
                <Box
                  color={textColor}
                  fontSize="sm"
                  sx={{
                    "& p": {
                      marginBottom: "8px",
                      "&:last-child": {
                        marginBottom: 0,
                      },
                    },
                    "& ul, & ol": {
                      paddingLeft: "16px",
                      marginBottom: "8px",
                    },
                    "& li": {
                      marginBottom: "4px",
                    },
                    "& code": {
                      backgroundColor: codeBg,
                      padding: "2px 4px",
                      borderRadius: "4px",
                      fontSize: "0.875em",
                    },
                    "& pre": {
                      backgroundColor: codeBg,
                      padding: "12px",
                      borderRadius: "8px",
                      overflow: "auto",
                      marginBottom: "8px",
                    },
                    "& h1, & h2, & h3, & h4, & h5, & h6": {
                      fontWeight: "bold",
                      marginBottom: "8px",
                    },
                    "& blockquote": {
                      borderLeft: "4px solid",
                      borderColor: blockquoteBorderColor,
                      paddingLeft: "12px",
                      fontStyle: "italic",
                      marginBottom: "8px",
                    },
                  }}
                >
                  <ReactMarkdown>{message.message}</ReactMarkdown>
                </Box>
              )}
            </Box>

            {message.timestamp && (
              <Text
                fontSize="xs"
                color={timestampColor}
                textAlign={isUser ? "right" : "left"}
                mt={1}
              >
                {moment(message.timestamp).format("HH:mm")}
              </Text>
            )}
          </VStack>
        </Box>
      </Flex>
    </Flex>
  );
};

export default ChatMessage;
